import { useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/UI/Button";
import useThemeStore from "@/store/theme";
import { toast } from "sonner";
import { copyToClipboard } from "@/utils/clipboard";

import light404Image from "@/assets/images/light-404-status.png";
import dark404Image from "@/assets/images/dark-404-status.png";

export default function NotFound() {
  const location = useLocation();
  const navigate = useNavigate();
  const { theme } = useThemeStore();

  const traceId = useMemo(() => {
    // 生成一个简单的 trace ID
    const array = new Uint8Array(16);
    crypto.getRandomValues(array);
    return Array.from(array, (byte) => byte.toString(16).padStart(2, "0")).join(
      ""
    );
  }, []);

  useEffect(() => {
    // 上报 404 到 Sentry (仅在生产环境)
    if (import.meta.env.PROD) {
      import("@sentry/react").then((Sentry) => {
        Sentry.captureException(new Error(`404: ${location.pathname}`), {
          extra: { traceId },
        });
      });
    }
  }, [location.pathname, traceId]);

  const handleCopyTraceId = async () => {
    const success = await copyToClipboard(traceId);
    if (success) {
      toast.success("已复制 Trace ID");
    } else {
      toast.error("复制失败");
    }
  };

  const handleGoHome = () => {
    navigate("/home");
  };

  const handleGoBack = () => {
    // 如果有历史记录则返回，否则去首页
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate("/home");
    }
  };

  const illustrationSrc = theme === "dark" ? dark404Image : light404Image;

  return (
    <div className="flex min-h-screen w-full items-center justify-center bg-surground">
      <div className="flex flex-col items-center">
        {/* 404 插图 */}
        <img
          src={illustrationSrc}
          alt="404"
          className="w-[600px] h-[280px] object-contain"
        />

        {/* 信息区域 */}
        <div className="flex flex-col items-center gap-8 pt-15">
          {/* 标题和描述 */}
          <div className="flex flex-col items-center gap-3">
            <h1 className="text-h1 text-primary">页面迷路了!</h1>
            <div className="flex flex-col items-center gap-1.5">
              <p className="text-body-lg text-secondary">
                由于路径变更或链接失效，我无法找到您请求的页面。
              </p>
              {/* Trace ID */}
              <div className="flex items-center gap-1.5 h-7 px-3 pr-0.5 bg-overlay-1 rounded-2">
                <span className="text-body-sm text-tertiary">Trace ID:</span>
                <span className="text-body-sm text-tertiary font-mono">
                  {traceId}
                </span>
                <Button
                  variant="tertiary"
                  size="smIcon"
                  className="text-tertiary hover:text-primary"
                  onClick={handleCopyTraceId}
                >
                  <iconpark-icon name="outline-copy" size="14" />
                </Button>
              </div>
            </div>
          </div>

          {/* 操作按钮 */}
          <div className="flex items-center gap-1">
            <Button variant="tertiary" size="lg" onClick={handleGoHome}>
              <iconpark-icon name="outline-home" size="16" />
              <span>首页</span>
            </Button>
            <Button
              variant="tertiaryBorder"
              size="lg"
              onClick={handleGoBack}
            >
              返回上一页
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
